import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { defineHandle } from '@/app/routes/types';

import { deliveryRidersAccessRoles } from './access';

const roles = [...deliveryRidersAccessRoles] as const;

/**
 * Unified Rider & delivery partner hub under `/delivery-riders/*`.
 * Live ops + dispatch + incentives live in `features/riders` (gateway `/admin/riders`),
 * agent master lives here (gateway `/admin/delivery-agents`).
 */
export const deliveryRidersRoutes: RouteObject[] = [
  {
    path: 'delivery-riders',
    async lazy() {
      const { DeliveryRidersLayout } = await import('./layouts/DeliveryRidersLayout');
      return { Component: DeliveryRidersLayout };
    },
    handle: defineHandle({
      roles: [...roles],
      breadcrumb: 'common:nav.deliveryRiders',
      title: 'common:nav.deliveryRiders',
    }),
    children: [
      {
        index: true,
        async lazy() {
          const { RiderOpsPage } = await import('@/features/riders');
          return { Component: RiderOpsPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'common:nav.ridersLive',
        }),
      },
      {
        path: 'attendance',
        async lazy() {
          const { AttendanceRegisterPage } = await import('@/features/riders');
          return { Component: AttendanceRegisterPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:attendancePage.title',
        }),
      },
      {
        path: 'search',
        async lazy() {
          const { RiderSearchPage } = await import('@/features/riders');
          return { Component: RiderSearchPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:searchPage.title',
        }),
      },
      {
        path: 'dispatch',
        async lazy() {
          const { DispatchPage } = await import('@/features/riders');
          return { Component: DispatchPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:dispatchPage.title',
        }),
      },
      {
        path: 'agents',
        async lazy() {
          const { AgentsPage } = await import('./pages/AgentsPage');
          return { Component: AgentsPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'deliveryRiders:list.title',
        }),
      },
      {
        path: 'agents/:authId',
        async lazy() {
          const { AgentDetailPage } = await import('./pages/AgentDetailPage');
          return { Component: AgentDetailPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'deliveryRiders:list.title',
        }),
      },
      {
        path: 'incentives',
        async lazy() {
          const { IncentivesPage } = await import('@/features/riders');
          return { Component: IncentivesPage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:incentives.title',
        }),
      },
      {
        path: 'petrol',
        async lazy() {
          const { PetrolRatePage } = await import('@/features/riders');
          return { Component: PetrolRatePage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:petrol.page.title',
        }),
      },
      {
        path: 'riders/:riderId',
        async lazy() {
          const { RiderProfilePage } = await import('@/features/riders');
          return { Component: RiderProfilePage };
        },
        handle: defineHandle({
          roles: [...roles],
          title: 'riders:profile.title',
        }),
      },
      {
        // Legacy `/delivery-riders/:authId` → agents detail
        path: ':authId',
        async lazy() {
          const { LegacyAgentRedirect } = await import('./pages/LegacyAgentRedirect');
          return { Component: LegacyAgentRedirect };
        },
      },
    ],
  },
  {
    path: 'riders/:riderId',
    async lazy() {
      const { RiderProfileRedirect } = await import('@/features/riders');
      return { Component: RiderProfileRedirect };
    },
  },
  {
    path: 'riders',
    element: <Navigate to="/delivery-riders" replace />,
  },
  {
    path: 'riders/*',
    element: <Navigate to="/delivery-riders" replace />,
  },
];
